<template>
  <div class="StoryFull">
    <story-admin-bar :story="story" />

    <article
      v-loading="fetchingStory"
      class="mx-auto max-w-[1040px] px-4 pb-4 pt-8 md:px-9 md:pt-10"
      itemtype="http://schema.org/NewsArticle"
      itemscope
    >
      <meta :content="url" itemprop="mainEntityOfPage">

      <div
        itemprop="publisher"
        itemscope=""
        itemtype="https://schema.org/Organization"
      >
        <meta itemprop="name" content="Hype.News">

        <div
          itemprop="logo"
          itemscope=""
          itemtype="https://schema.org/ImageObject"
        >
          <meta :content="$asset('/assets/images/logo_234x60.png')" itemprop="url">
          <meta itemprop="width" content="234">
          <meta itemprop="height" content="60">
        </div>

        <link itemprop="sameAs" href="https://hype.news">
      </div>

      <!-- Kicker: newsroom identity, left-aligned, above the hero -->
      <div
        v-if="newsroom.id"
        itemprop="author"
        itemscope=""
        itemtype="https://schema.org/Organization"
        class="mb-5 flex items-center gap-2.5"
      >
        <meta :content="newsroom.name" itemprop="name">

        <router-link
          :to="{ name: 'newsroom.stories', params: { newsroomSlug: newsroom.slug } }"
          class="shrink-0"
        >
          <round-avatar
            :src="newsroomLogo"
            :alt="newsroom.name"
            :acronym-fallback="newsroom.name"
            :main-color="newsroom.main_color"
            size="xsmall"
          />
        </router-link>

        <router-link
          :to="{ name: 'newsroom.stories', params: { newsroomSlug: newsroom.slug } }"
          class="font-display text-sm font-bold text-ink transition-colors hover:text-accent"
        >
          {{ newsroom.name }}
        </router-link>

        <span
          v-if="primaryCategory"
          class="rounded-full bg-accent-soft px-3 py-1 font-display text-xs font-bold text-accent"
        >
          {{ $t('categories.' + primaryCategory.id) }}
        </span>
      </div>

      <!-- Hero -->
      <figure v-if="storyCroppedCover" class="m-0">
        <div
          :style="{ backgroundImage: `url(${storyCroppedCover})` }"
          itemprop="image"
          itemscope
          itemtype="http://schema.org/ImageObject"
          class="aspect-[16/8] w-full rounded-card bg-[#dfe6e3] bg-cover bg-center shadow-[0_14px_34px_-20px_rgba(22,34,30,.4)]"
        >
          <meta :content="storyCroppedCover" itemprop="url">
          <meta itemprop="width" content="900">
          <meta itemprop="height" content="506">
        </div>

        <figcaption
          v-if="story.cover_caption"
          class="mt-2.5 font-sans text-base text-muted-soft"
        >
          {{ story.cover_caption }}
        </figcaption>
      </figure>

      <!-- Body: sticky rail + article column -->
      <div class="mt-8 grid grid-cols-1 gap-8 md:grid-cols-[200px_minmax(0,1fr)] md:items-start md:gap-11">
        <!-- Rail (Categories / Tags / Share) -->
        <aside class="order-2 md:order-1 md:sticky md:top-6 md:self-start">
          <template v-if="topCategories.length">
            <div class="mb-2.5 font-display text-xs font-bold uppercase tracking-wider text-muted-faint">
              {{ $t('pages.story.categories') }}
            </div>
            <story-categories-list
              :categories="topCategories"
              class="mb-6"
            />
          </template>

          <template v-if="tags.length">
            <div class="mb-3 font-display text-xs font-bold uppercase tracking-wider text-muted-faint">
              {{ $t('pages.story.tags') }}
            </div>
            <story-tags-list
              :tags="tags"
              tag-class="rounded-full border border-solid border-line bg-white px-3 py-1.5 font-display text-xs font-semibold text-muted"
              class="mb-6"
            />
          </template>

          <div class="mb-3 font-display text-xs font-bold uppercase tracking-wider text-muted-faint">
            {{ $t('pages.story.share') }}
          </div>
          <ShareOn
            :url="url"
            :title="story.title"
            :description="story.boilerplate"
          />
        </aside>

        <!-- Article column -->
        <div class="order-1 min-w-0 md:order-2 md:max-w-[680px]">
          <!-- Headline block — sits in the article column, aligned with the body text -->
          <header class="mb-8">
            <h1
              itemprop="headline"
              class="font-display text-3xl font-extrabold leading-tight tracking-heading text-ink md:text-[42px] md:leading-[1.06]"
            >
              {{ story.headline }}
            </h1>

            <p
              v-if="story.subheadline"
              itemprop="alternativeHeadline"
              class="mt-4 font-sans text-lg leading-relaxed text-muted"
              v-html="story.subheadline"
            />

            <div class="mt-5 flex flex-wrap items-center gap-x-2 font-display text-sm font-semibold text-muted-faint">
              <span v-if="story.type" title="Story Type">
                {{ $t('components.story_types.' + story.type) }}
              </span>
              <span v-if="story.type" aria-hidden="true">·</span>
              <time
                :datetime="story.published_at"
                itemprop="datePublished"
                title="Published On"
              >
                {{ datetime(story.published_at, 'DD MMMM YYYY') }}
              </time>
              <meta :content="story.updated_at" itemprop="dateModified">
            </div>
          </header>

          <div
            itemprop="articleBody"
            class="StoryFull__prose fr-view"
            v-html="story.copytext"
          />

          <div
            v-if="story.boilerplate"
            class="StoryFull__prose mt-8 text-base text-muted"
            v-html="story.boilerplate"
          />

          <div
            v-if="story.contact"
            class="StoryFull__prose mt-4 text-base text-muted"
            v-html="story.contact"
          />

          <!-- Contacts -->
          <div v-if="contacts.length" class="mt-10">
            <div class="mb-5 h-px bg-line" />
            <div class="mb-5">
              <h2 class="font-display text-lg font-bold tracking-heading text-ink">
                {{ $t('pages.story.contacts') }}
              </h2>
              <p class="mt-1 text-base text-muted">
                {{ $t('pages.story.for_more_info') }}
              </p>
            </div>

            <div class="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              <contact-card
                v-for="contact in contacts"
                :key="contact.id"
                :contact="contact"
              />
            </div>
          </div>

          <!-- Attachments -->
          <div v-if="hasAttachments" class="StoryFull__attachments attachments mt-10">
            <div class="mb-5 h-px bg-line" />
            <h2 class="mb-4 font-display text-lg font-bold tracking-heading text-ink">
              {{ $t('pages.story.attachments') }}
            </h2>
            <Attachments :files="story.files.data" />
          </div>

          <!-- Inline newsroom Follow CTA -->
          <div
            v-if="newsroom.id"
            class="mt-8 flex items-center gap-3.5 rounded-card border border-solid border-line bg-white p-4 shadow-sm"
          >
            <router-link
              :to="{ name: 'newsroom.stories', params: { newsroomSlug: newsroom.slug } }"
              class="shrink-0"
            >
              <round-avatar
                :src="newsroomLogo"
                :alt="newsroom.name"
                :acronym-fallback="newsroom.name"
                :main-color="newsroom.main_color"
                size="medium"
              />
            </router-link>

            <div class="min-w-0 flex-1">
              <router-link
                :to="{ name: 'newsroom.stories', params: { newsroomSlug: newsroom.slug } }"
                class="block font-display text-base font-bold text-ink transition-colors hover:text-accent"
              >
                {{ newsroom.name }}
              </router-link>
              <div
                v-if="newsroomCountsLabel"
                class="font-display text-xs font-medium text-muted-soft"
              >
                {{ newsroomCountsLabel }}
              </div>
            </div>

            <follow-newsroom-button :newsroom="newsroom" class="shrink-0" />
          </div>
        </div>
      </div>
    </article>

    <!-- Related stories -->
    <section
      v-if="hasRelatedStories"
      class="mx-auto max-w-[1040px] px-4 pb-11 md:px-9"
    >
      <div class="h-px bg-line" />
      <div class="mb-4.5 mt-7 flex items-baseline justify-between">
        <h2 class="font-display text-xl font-extrabold tracking-heading text-ink">
          {{ $t('pages.story.related_stories') }}
        </h2>
        <router-link
          :to="$i18nRoute({ name: 'home' })"
          class="shrink-0 font-display text-base font-semibold text-accent transition-opacity hover:opacity-80"
        >
          {{ $t('pages.story.more_from_read') }} →
        </router-link>
      </div>

      <div class="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <story
          v-for="relatedStory in relatedStories"
          :key="relatedStory.id"
          :story="relatedStory"
          :show-less-info="false"
        />
      </div>
    </section>
  </div>
</template>

<script>
import ContactCard from '@/components/core/ContactCard'
import Story from '@/components/storySearch/StoryCard.vue'
import { changePageTitle, changePageDescription, changeOpenGraphTag, load, persist } from '@/shared/utils'
import StoryCategoriesList from '@/shared/js/components/story/StoryCategoriesList'
import StoryTagsList from '@/shared/js/components/story/StoryTagsList'
import { mapMutations } from 'vuex'
import ShareOn from '@/components/ShareOn'
import StoryAdminBar from '../components/newsroomEdit/StoryAdminBar'
import { StoryApiService } from '@/shared/js/services/api/StoryApiService'
import DatetimeMixin from '@/shared/js/mixins/DatetimeMixin'

export default {
  components: {
    StoryTagsList,
    StoryCategoriesList,
    StoryAdminBar,
    Story,
    ContactCard,
    ShareOn
  },

  mixins: [DatetimeMixin],

  data () {
    return {
      story: {
        cover: {},
        categories: {
          data: []
        },
        tags: {
          data: []
        },
        contacts: {
          data: []
        }
      },
      relatedStories: [],

      newsroom: {},
      fetchingStory: true,
      cancelToken: null
    }
  },

  computed: {
    /**
     * Determines if the story has attachments.
     *
     * @return Boolean
     */
    hasAttachments () {
      let attachments = this.story.files

      return attachments && attachments.data.length
    },

    /**
     * Determines if the story has related stories.
     *
     * @return Boolean
     */
    hasRelatedStories () {
      return this.relatedStories.length > 0
    },

    /**
     * Determines if the current route is the story route.
     *
     * @return Boolean
     */
    isStoryRoute () {
      return (this.$route.name || '').startsWith('story')
    },

    /**
     * Returns the current url.
     *
     * @return String
     */
    url () {
      return this.$getUrl(this.$route.fullPath, 'hypenews', null)
    },

    tags () {
      return this.story.tags.data || []
    },

    topCategories () {
      return this.story.categories.data.filter(cat => cat.depth === 2)
    },

    /**
     * The story's broadest category — used for the kicker pill above the hero.
     * Falls back to the lowest-depth category available.
     *
     * @return {Object|null}
     */
    primaryCategory () {
      const categories = this.$safeGet(this.story, 'categories.data', [])
      if (!categories.length) return null
      return [...categories].sort((a, b) => a.depth - b.depth)[0]
    },

    storyCroppedCover () {
      return this.largeImage || this.croppedImagePath
    },

    contacts () {
      return this.story.contacts.data || []
    },

    newsroomLogo () {
      return this.$safeGet(this.newsroom, 'logo.cropped')
    },

    /**
     * The CTA card's "N followers · M stories" line — built only from counts
     * the API actually returned. The mockup's "1,240 followers · 48 stories"
     * are placeholders, so an empty line renders nothing (S10 rule).
     *
     * @return String
     */
    newsroomCountsLabel () {
      const parts = []
      if (this.newsroom.followers_count > 0) {
        parts.push(`${this.newsroom.followers_count.toLocaleString()} ${this.$t('components.company_sidebar.followers')}`)
      }
      if (this.newsroom.stories_count > 0) {
        parts.push(`${this.newsroom.stories_count} ${this.$t('components.company_sidebar.stories')}`)
      }
      return parts.join(' · ')
    },

    largeImage () {
      return this.$safeGet(this.story, 'cover.scaled.large.path', null)
    },

    croppedImagePath () {
      return this.$safeGet(this.story, 'cover.cropped', null)
    }
  },

  watch: {
    '$route': function () {
      if (this.isStoryRoute) {
        this.fetchStoryData(this.$route.params.storySlug)
      }
    }
  },

  mounted () {
    this.fetchStoryData(this.$route.params.storySlug)
  },

  beforeDestroy () {
    this.cancelToken && this.cancelToken.cancel()
  },

  methods: {
    ...mapMutations({
      setStory: 'SET_STORY'
    }),
    async fetchStoryData (slug) {
      this.startLoad()

      this.cancelToken && this.cancelToken.cancel()
      this.cancelToken = this.$api.cancelToken()

      this.fetchingStory = true
      try {
        await Promise.all([
          this.fetchStory(slug),
          this.fetchRelatedStories(slug)
        ])
        const legalCookie = load('LEGAL_WARNING', false)

        if (this.newsroom.legal_warning && !legalCookie) {
          try {
            await this.$confirm(this.$t('components.full_story.legal_warning'))
            persist('LEGAL_WARNING', true)
          } catch (e) {
            this.$router.push({ name: 'newsroom.stories', params: { newsroomSlug: this.newsroom.slug } })
          }
        }

        this.stopLoad()
      } catch (err) {
        if (this.$api.isCancelToken(err)) return
        this.$router.push(this.$i18nRoute({ name: 'home' }))
        this.stopLoadWithFail()
      } finally {
        this.fetchingStory = false
      }
    },

    fetchStory (slug) {
      return StoryApiService.fetchStory(slug, {
        params: {
          include: ['newsroom.country', 'files', 'categories', 'tags', 'contacts']
        },
        cancelToken: this.cancelToken.token
      })
        .then((story) => {
          this.story = story

          this.setStory(story)

          this.newsroom = story.newsroom.data

          this.changePageMetaTags(story)
        })
        .catch((error) => {
          if (this.$api.isCancelToken(error)) throw error
          this.$displayRequestError(error, this.$t('errors.error'), 5000, [404]) // skip reporting 404's
          throw error
        })
    },

    async fetchRelatedStories (slug) {
      this.relatedStories = await StoryApiService.fetchRelatedStories(slug, {
        params: {
          limit: 4,
          include: 'newsroom.country'
        },
        cancelToken: this.cancelToken.token
      })
    },

    changePageMetaTags (story) {
      changePageTitle(story.headline)
      changePageDescription(story.excerpt)
      changeOpenGraphTag('title', story.headline)
      changeOpenGraphTag('type', 'article')
      changeOpenGraphTag('url', this.url)
      if (this.$safeGet(story, 'cover.cropped', false)) {
        changeOpenGraphTag('image', story.cover.cropped)
      }
      changeOpenGraphTag('description', story.copytext)
    }
  }
}
</script>

<style src="froala-editor/css/froala_style.css"></style>

<!-- Shared house prose typography (extracted S12b so EmbeddedStory reuses one
     source). Non-scoped by design — see the file header. -->
<style lang="scss" src="@/assets/styles/components/story-prose.scss"></style>
